import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import StatusBadge from '../../components/StatusBadge';

const fmt = (d) => (d ? new Date(d).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// The webhook log: what we sent Ivipass and what Ivipass sent us.
const Deliveries = () => {
  const [direction, setDirection] = useState('out');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const p = new URLSearchParams({ direction, page: String(page) });
      if (direction === 'out' && status) p.set('status', status);
      const r = await api(`/admin/webhooks?${p}`, { auth: true });
      setData(r.data);
    } catch (e) { setError(e.message); }
  }, [direction, status, page]);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    setFlash('');
    try {
      await api(`/admin/webhooks/${id}/resend`, { method: 'POST', auth: true });
      setFlash('Queued. It will be sent within a minute.');
      load();
    } catch (e) { setError(e.message); }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div>
      <h1 className="text-2xl font-bold">Deliveries</h1>
      <p className="mt-1 text-sm text-muted">Messages between this site and Ivipass. Failed messages to Ivipass are retried automatically five times, then wait here for you to resend.</p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {[['out', 'Sent to Ivipass'], ['in', 'Received from Ivipass']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => { setDirection(k); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm ${direction === k ? 'bg-primary text-white' : 'bg-white text-muted hover:text-ink'}`}>{label}</button>
        ))}
        {direction === 'out' && (
          <select className="wc-input ml-auto max-w-[12rem]" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">Any status</option>
            <option value="pending">Pending</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
          </select>
        )}
      </div>
      {flash && <p className="mt-4 rounded-lg bg-success-soft p-3 text-sm text-success">{flash}</p>}
      {error && <p className="mt-4 rounded-lg bg-danger-soft p-3 text-sm text-danger">{error}</p>}

      <div className="wc-card mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Contract</th>
              <th className="px-4 py-3">When</th>
              {direction === 'out' && <th className="px-4 py-3">Status</th>}
              {direction === 'out' && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {data && data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">Nothing yet.</td></tr>}
            {data && data.items.map((w) => (
              <tr key={w._id} className="border-b border-line last:border-0">
                <td className="px-4 py-3">{w.type}</td>
                <td className="px-4 py-3"><code className="text-xs">{w.subjectRef || '—'}</code></td>
                <td className="px-4 py-3">{fmt(direction === 'in' ? (w.receivedAt || w.createdAt) : (w.deliveredAt || w.lastAttemptAt || w.createdAt))}</td>
                {direction === 'out' && (
                  <td className="px-4 py-3">
                    <StatusBadge status={w.status} />
                    <div className="text-xs text-muted">{w.attempts} attempt{w.attempts === 1 ? '' : 's'}{w.lastStatusCode ? ` · HTTP ${w.lastStatusCode}` : ''}{w.lastError ? ` · ${w.lastError}` : ''}</div>
                  </td>
                )}
                {direction === 'out' && (
                  <td className="px-4 py-3 text-right">
                    {w.status === 'failed' && <button type="button" className="text-sm text-primary underline" onClick={() => resend(w._id)}>Resend</button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <button type="button" className="wc-btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="text-muted">Page {page} of {pages}</span>
          <button type="button" className="wc-btn-ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default Deliveries;
